import { forwardRef } from 'react'

import { DateInput, EmailInput, RadioButton, TextInput } from 'slab'

import { type CoveredParticipant, type OtherDependentInfo, type PurchaserType } from './index'

type PurchaserSelectionProps = {
  purchaserType: PurchaserType;
  onPurchaserTypeChange: (type: PurchaserType) => void;
  selectedParticipantId: string | null;
  onSelectedParticipantIdChange: (id: string | null) => void;
  otherDependentInfo: Partial<OtherDependentInfo>;
  onOtherDependentInfoChange: (info: Partial<OtherDependentInfo>) => void;
  coveredParticipants: CoveredParticipant[];
  showValidation?: boolean;
}

export const PurchaserSelection = forwardRef<HTMLFormElement, PurchaserSelectionProps>(({
  purchaserType,
  onPurchaserTypeChange,
  selectedParticipantId,
  onSelectedParticipantIdChange,
  otherDependentInfo,
  onOtherDependentInfoChange,
  coveredParticipants,
  showValidation = false,
}, ref) => {
  const handleFieldChange = (field: keyof OtherDependentInfo) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    onOtherDependentInfoChange({ ...otherDependentInfo, [field]: e.target.value })
  }

  const handleParticipantSelection = (participant: CoveredParticipant) => {
    onSelectedParticipantIdChange(participant.id)
    if (participant.relationship === 'self') {
      onPurchaserTypeChange('myself')
    } else if (participant.relationship === 'spouse') {
      onPurchaserTypeChange('spouse')
    } else if (participant.relationship === 'child') {
      onPurchaserTypeChange('child')
    }
  }

  const spouseParticipants = coveredParticipants.filter(p => p.relationship === 'spouse')
  const childParticipants = coveredParticipants.filter(p => p.relationship === 'child')

  return (
    <form ref={ref} className='flex flex-col gap-5 w-full'>
      <h2 className='heading-sm'>
        Who are you purchasing for?
      </h2>

      <div className='flex flex-col gap-3 py-2'>
        <RadioButton
          label='Myself'
          name='purchaser'
          value='myself'
          checked={purchaserType === 'myself'}
          onChange={() => { onPurchaserTypeChange('myself'); onSelectedParticipantIdChange(null) }}
        />

        {spouseParticipants.map((spouse) => (
          <RadioButton
            key={spouse.id}
            label={spouse.full_name}
            name='purchaser'
            value={spouse.id}
            checked={selectedParticipantId === spouse.id}
            onChange={() => handleParticipantSelection(spouse)}
          />
        ))}

        {childParticipants.map((child) => (
          <RadioButton
            key={child.id}
            label={child.full_name}
            name='purchaser'
            value={child.id}
            checked={selectedParticipantId === child.id}
            onChange={() => handleParticipantSelection(child)}
          />
        ))}

        <RadioButton
          label='Other dependent'
          name='purchaser'
          value='other_dependent'
          checked={purchaserType === 'other_dependent'}
          onChange={() => { onPurchaserTypeChange('other_dependent'); onSelectedParticipantIdChange(null) }}
        />
      </div>

      {purchaserType === 'other_dependent' && (
        <div className='ml-7 flex flex-col gap-4 self-stretch'>
          <div className='flex items-start self-stretch gap-4'>
            <div className='flex-1'>
              <TextInput
                label='First name'
                value={otherDependentInfo.first_name || ''}
                onChange={handleFieldChange('first_name')}
                required={showValidation}
                autoComplete='given-name'
                autoFocus
              />
            </div>
            <div className='flex-1'>
              <TextInput
                label='Last name'
                value={otherDependentInfo.last_name || ''}
                onChange={handleFieldChange('last_name')}
                required={showValidation}
                autoComplete='family-name'
              />
            </div>
          </div>
          <div className='flex items-start self-stretch gap-4'>
            <div className='flex-1'>
              <DateInput
                label='Date of birth'
                value={otherDependentInfo.date_of_birth || ''}
                onChange={handleFieldChange('date_of_birth')}
                required={showValidation}
                autoComplete='bday'
              />
            </div>
            <div className='flex-1'>
              <EmailInput
                label='Email'
                value={otherDependentInfo.email || ''}
                onChange={handleFieldChange('email')}
                required={showValidation}
                autoComplete='email'
              />
            </div>
          </div>
        </div>
      )}
    </form>
  )
})
